import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import client from '../../../lib/feathers';

export default function IndustryPassportsInfoPanel() {
  const navigate = useNavigate();
  const listing = useSelector((s) => s.listings.selected);
  const [listingPassports, setListingPassports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    completionThreshold: 5,
    status: 'draft'
  });

  const loadListingPassports = async () => {
    if (!listing?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const service = client.service('industry/listing-passport');
      const result = await service.find({
        query: {
          listingId: listing.id,
        },
      });
      setListingPassports(result.data || []);
    } catch (e) {
      console.error('Failed to load listing passports:', e);
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadListingPassports();
  }, [listing?.id]);

  const handleCreatePassport = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);

    try {
      // Create the passport
      const passportService = client.service('industry/passport');
      const passport = await passportService.create(formData);

      // Add the current listing to the passport
      const listingPassportService = client.service('industry/listing-passport');
      await listingPassportService.create({
        passportId: passport.id,
        listingId: listing.id,
        accepted: true,
        acceptedAt: new Date().toISOString()
      });

      setShowCreateModal(false);
      setFormData({
        title: '',
        description: '',
        startDate: '',
        endDate: '',
        completionThreshold: 5,
        status: 'draft'
      });

      // Navigate to the new passport
      navigate(`/industry/listings/${listing.id}/passports/${passport.id}`);
    } catch (err) {
      console.error('Error creating passport:', err);
      setCreateError(err.message || 'Failed to create passport');
    } finally {
      setCreating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      draft: 'bg-gray-100 text-gray-800',
      active: 'bg-green-100 text-green-800',
      ended: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-red-100 text-red-800',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[status] || statusStyles.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-red-600">
          Failed to load passports:
          {' '}
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-start">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Passports
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Wine passports that include your listing. Accept participation to be visible to passport users.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-icon hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-icon"
        >
          <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
          Create Passport
        </button>
      </div>

      {listingPassports.length === 0 ? (
        <div className="px-4 py-12 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No passport invitations</h3>
          <p className="mt-1 text-sm text-gray-500">
            Your listing hasn&apos;t been added to any wine passports yet.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-200">
          {listingPassports.map((lp) => (
            <li key={lp.id} className="px-4 py-4 sm:px-6">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-medium text-gray-900">
                      {lp.passport?.title || 'Untitled Passport'}
                    </p>
                    {getStatusBadge(lp.passport?.status || 'draft')}
                  </div>
                  <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                    <span>
                      {formatDate(lp.passport?.startDate)}
                      {' - '}
                      {formatDate(lp.passport?.endDate)}
                    </span>
                    {lp.order != null && (
                      <span>
                        Stop #
                        {lp.order + 1}
                      </span>
                    )}
                  </div>
                  {lp.subtitle && (
                    <p className="mt-1 text-sm text-gray-600 italic">
                      &ldquo;
                      {lp.subtitle}
                      &rdquo;
                    </p>
                  )}
                </div>

                <div className="ml-4 flex-shrink-0 flex items-center gap-2">
                  {lp.accepted ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Accepted
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      Pending
                    </span>
                  )}
                  <Link
                    to={`/industry/listings/${listing.id}/passports/${lp.passportId}`}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Create Passport Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowCreateModal(false)} />

            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Create New Passport</h2>
              <p className="text-sm text-gray-500 mb-4">
                This will create a new passport and add &ldquo;{listing?.name}&rdquo; as the first stop.
              </p>

              {createError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-800">{createError}</p>
                </div>
              )}

              <form onSubmit={handleCreatePassport}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                      Title *
                    </label>
                    <input
                      type="text"
                      id="title"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-icon focus:border-icon sm:text-sm"
                      placeholder="e.g., 2026 Spring Wine Trail"
                    />
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      id="description"
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-icon focus:border-icon sm:text-sm"
                      placeholder="Describe your wine passport..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                        Start Date *
                      </label>
                      <input
                        type="date"
                        id="startDate"
                        required
                        value={formData.startDate}
                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-icon focus:border-icon sm:text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                        End Date *
                      </label>
                      <input
                        type="date"
                        id="endDate"
                        required
                        value={formData.endDate}
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-icon focus:border-icon sm:text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="completionThreshold" className="block text-sm font-medium text-gray-700">
                      Stops Required to Complete *
                    </label>
                    <input
                      type="number"
                      id="completionThreshold"
                      required
                      min="1"
                      value={formData.completionThreshold}
                      onChange={(e) => setFormData({ ...formData, completionThreshold: parseInt(e.target.value, 10) })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-icon focus:border-icon sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Number of venues users must visit to complete the passport
                    </p>
                  </div>
                </div>

                {/* Info about next steps */}
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-sm text-blue-700">
                    After creating the passport, you&apos;ll be able to add a cover image, manage listings, and configure additional settings.
                  </p>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    disabled={creating}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-icon hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-icon disabled:opacity-50"
                  >
                    {creating ? 'Creating...' : 'Create Passport'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
